import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlusCircle, FiTrash2, FiChevronDown, FiChevronRight } from "react-icons/fi";
import feeStructureService from "../../../services/feeStructureService";
import { academicYearService } from "../../../services/yearService";
import courseService from "../../../services/courseService";
import feeHeadService from "../../../services/feeHeadService";
import { getYearOptions } from "../../../utils/courseYears";

const ADMISSION_CATEGORY_OPTIONS = [
    "Open", "OBC", "SC", "ST", "NT-A", "NT-B", "NT-C", "NT-D",
    "VJ", "SBC", "TFWS", "Open Minority Scholarship", "EWS",
];
const STUDENT_TYPE_OPTIONS = ["New Student", "Old Student"];
const FEE_TYPE_OPTIONS = ["Non-refundable", "Refundable"];

let idCounter = 0;
const nextLocalId = () => `local-${Date.now()}-${idCounter++}`;

const emptyOverride = () => ({
    localId: nextLocalId(),
    admissionCategory: "",
    studentType: "",
    amount: "",
});

const emptyHead = () => ({
    localId: nextLocalId(),
    feeHead: "",
    feeType: "Non-refundable",
    defaultAmount: "",
    overrides: [],
    expanded: true,
});

export default function FeeStructureForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [academicYears, setAcademicYears] = useState([]);
    const [academicYear, setAcademicYear] = useState("");
    const [courses, setCourses] = useState([]);
    const [courseId, setCourseId] = useState("");
    const [yearNumber, setYearNumber] = useState("");

    const [feeHeadsMaster, setFeeHeadsMaster] = useState([]);
    const [showNewHeadModal, setShowNewHeadModal] = useState(false);
    const [newHeadData, setNewHeadData] = useState({
        name: "",
        category: "Academic",
        feeType: "Non-refundable",
    });
    const [savingNewHead, setSavingNewHead] = useState(false);

    const selectedCourse = courses.find((c) => c._id === courseId);
    const yearOptions = selectedCourse ? getYearOptions(selectedCourse) : [];
    const [heads, setHeads] = useState([emptyHead()]);

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);
    const initialized = useRef(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await academicYearService.getAll();
                setAcademicYears(res.data || []);
            } catch (err) {
                toast.error("Couldn't load academic years: " + err.message);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await courseService.getAll({ isActive: true });
                setCourses(res.data || []);
            } catch (err) {
                toast.error("Couldn't load courses: " + err.message);
            }
        })();
    }, []);

    const fetchFeeHeads = async () => {
        try {
            const res = await feeHeadService.getAll();
            setFeeHeadsMaster(res.data?.data || res.data || []);
        } catch (err) {
            console.error("Couldn't load fee heads:", err.message);
        }
    };

    useEffect(() => {
        fetchFeeHeads();
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const res = await feeStructureService.getById(id);
                const structure = res.data;
                setAcademicYear(structure.academicYear?._id || structure.academicYear || "");
                setCourseId(structure.course?._id || structure.course || "");
                setYearNumber(structure.yearNumber ? String(structure.yearNumber) : "");

                setHeads(
                    (structure.heads || []).map((h) => ({
                        localId: h._id || nextLocalId(),
                        _id: h._id,
                        feeHead: h.feeHead?._id || h.feeHead || "",
                        feeType: h.feeType || "Non-refundable",
                        defaultAmount: h.defaultAmount ?? "",
                        expanded: false,
                        overrides: (h.overrides || []).map((o) => ({
                            localId: nextLocalId(),
                            admissionCategory: o.admissionCategory || "",
                            studentType: o.studentType || "",
                            amount: o.amount ?? "",
                        })),
                    }))
                );
            } catch (err) {
                toast.error(err.message);
                navigate("/settings/fee-structures");
            } finally {
                setFetching(false);
                initialized.current = true;
            }
        })();
    }, [id, isEdit, navigate]);

    const addHead = () => setHeads((prev) => [...prev, emptyHead()]);
    const removeHead = (localId) => setHeads((prev) => prev.filter((h) => h.localId !== localId));
    const updateHead = (localId, field, value) =>
        setHeads((prev) => prev.map((h) => (h.localId === localId ? { ...h, [field]: value } : h)));
    const toggleHeadExpanded = (localId) =>
        setHeads((prev) => prev.map((h) => (h.localId === localId ? { ...h, expanded: !h.expanded } : h)));

    const handleHeadSelect = (localId, val) => {
        if (val === "__NEW__") {
            setShowNewHeadModal(true);
            return;
        }
        const selectedHead = feeHeadsMaster.find((fh) => fh._id === val);
        setHeads((prev) =>
            prev.map((h) =>
                h.localId === localId
                    ? {
                        ...h,
                        feeHead: val,
                        feeType: selectedHead?.feeType || h.feeType,
                    }
                    : h
            )
        );
    };

    const handleCreateMasterHead = async (e) => {
        e.preventDefault();
        if (!newHeadData.name.trim()) return toast.error("Head name is required");

        setSavingNewHead(true);
        try {
            const res = await feeHeadService.create(newHeadData);
            const created = res.data?.data || res.data;
            toast.success(`Fee Head "${created.name}" created`);
            await fetchFeeHeads();
            setShowNewHeadModal(false);
            setNewHeadData({ name: "", category: "Academic", feeType: "Non-refundable" });

            setHeads((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && !last.feeHead) {
                    last.feeHead = created._id;
                    last.feeType = created.feeType;
                }
                return updated;
            });
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSavingNewHead(false);
        }
    };

    const addOverride = (headLocalId) =>
        setHeads((prev) =>
            prev.map((h) => (h.localId === headLocalId ? { ...h, overrides: [...h.overrides, emptyOverride()] } : h))
        );
    const removeOverride = (headLocalId, overrideLocalId) =>
        setHeads((prev) =>
            prev.map((h) =>
                h.localId === headLocalId
                    ? { ...h, overrides: h.overrides.filter((o) => o.localId !== overrideLocalId) }
                    : h
            )
        );
    const updateOverride = (headLocalId, overrideLocalId, field, value) =>
        setHeads((prev) =>
            prev.map((h) =>
                h.localId === headLocalId
                    ? {
                        ...h,
                        overrides: h.overrides.map((o) =>
                            o.localId === overrideLocalId ? { ...o, [field]: value } : o
                        ),
                    }
                    : h
            )
        );

    const validate = () => {
        const nextErrors = {};
        if (!academicYear) nextErrors.academicYear = "Academic year is required";
        if (!courseId) nextErrors.courseId = "Course is required";
        if (!yearNumber) nextErrors.yearNumber = "Course year is required";
        if (heads.length === 0) nextErrors.heads = "At least one fee head is required";
        heads.forEach((h) => {
            if (!h.feeHead) nextErrors.heads = "Every fee head must be selected from the catalog";
            if (h.defaultAmount === "" || Number(h.defaultAmount) < 0) {
                nextErrors.heads = "Every fee head needs a valid default amount";
            }
            h.overrides.forEach((o) => {
                if (!o.admissionCategory && !o.studentType) {
                    nextErrors.heads = "Each override needs at least a category or student type";
                }
                if (o.amount === "" || Number(o.amount) < 0) {
                    nextErrors.heads = "Each override needs a valid amount";
                }
            });
        });
        return nextErrors;
    };

    const defaultTotal = heads.reduce((sum, h) => sum + (Number(h.defaultAmount) || 0), 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nextErrors = validate();
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            toast.error(Object.values(nextErrors)[0]);
            return;
        }

        const payload = {
            academicYear,
            course: courseId,
            yearNumber: Number(yearNumber),
            heads: heads.map((h) => ({
                feeHead: h.feeHead,
                feeType: h.feeType,
                defaultAmount: Number(h.defaultAmount),
                overrides: h.overrides.map((o) => ({
                    admissionCategory: o.admissionCategory || null,
                    studentType: o.studentType || null,
                    amount: Number(o.amount),
                })),
            })),
        };

        setLoading(true);
        try {
            let res;
            if (isEdit) {
                res = await feeStructureService.update(id, payload);
            } else {
                res = await feeStructureService.create(payload);
            }
            toast.success(`Fee structure saved. ${res.data?.syncedCount || 0} student(s) synced.`);
            navigate("/settings/fee-structures");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="card">
                <div className="loading-center">
                    <div className="spinner" />
                    Loading fee structure...
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Fee Structure" : "Add Fee Structure"}</h1>
                    <p className="page-subtitle">
                        Define fee heads per course and year.
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card" style={{ marginBottom: "1.25rem" }}>
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">
                                    Academic Year<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className={`form-control ${errors.academicYear ? "error" : ""}`}
                                    value={academicYear}
                                    onChange={(e) => setAcademicYear(e.target.value)}
                                >
                                    <option value="">Select Academic Year</option>
                                    {academicYears.map((y) => (
                                        <option key={y._id} value={y._id}>
                                            {y.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.academicYear && <span className="form-error">{errors.academicYear}</span>}
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    Course<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className={`form-control ${errors.courseId ? "error" : ""}`}
                                    value={courseId}
                                    onChange={(e) => {
                                        setCourseId(e.target.value);
                                        setYearNumber("");
                                    }}
                                >
                                    <option value="">Select Course</option>
                                    {courses.map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.shortName}
                                        </option>
                                    ))}
                                </select>
                                {errors.courseId && <span className="form-error">{errors.courseId}</span>}
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    Course Year<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className={`form-control ${errors.yearNumber ? "error" : ""}`}
                                    value={yearNumber}
                                    onChange={(e) => setYearNumber(e.target.value)}
                                    disabled={!courseId}
                                >
                                    <option value="">{courseId ? "Select Year" : "Select a course first"}</option>
                                    {yearOptions.map((y) => (
                                        <option key={y.yearNumber} value={y.yearNumber}>
                                            {y.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.yearNumber && <span className="form-error">{errors.yearNumber}</span>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div
                        className="card-header"
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                        <strong>Fee Heads</strong>
                        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                            Default Total: <strong>₹{defaultTotal.toLocaleString("en-IN")}</strong>
                        </span>
                    </div>
                    <div className="card-body" style={{ display: "grid", gap: "0.85rem" }}>
                        {heads.map((head, idx) => {
                            const headDoc = feeHeadsMaster.find((fh) => fh._id === head.feeHead);
                            return (
                                <div key={head.localId} style={{ border: "1px solid var(--border)", borderRadius: 10 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.6rem",
                                            padding: "0.65rem 0.85rem",
                                            cursor: "pointer",
                                            background: "#f8fafc",
                                            borderRadius: "10px 10px 0 0",
                                        }}
                                        onClick={() => toggleHeadExpanded(head.localId)}
                                    >
                                        {head.expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                                        <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                                            {headDoc?.name || `Head #${idx + 1}`}
                                        </span>
                                        {headDoc?.category && (
                                            <span className="badge badge-blue" style={{ fontSize: 11 }}>
                                                {headDoc.category}
                                            </span>
                                        )}
                                        {head.defaultAmount !== "" && (
                                            <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                                                ₹{Number(head.defaultAmount || 0).toLocaleString("en-IN")}
                                            </span>
                                        )}
                                        {head.overrides.length > 0 && (
                                            <span className="badge badge-blue">{head.overrides.length} override(s)</span>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-outline btn-sm"
                                            style={{ marginLeft: "auto" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeHead(head.localId);
                                            }}
                                        >
                                            <FiTrash2 size={13} />
                                        </button>
                                    </div>

                                    {head.expanded && (
                                        <div style={{ padding: "0.85rem" }}>
                                            <div className="form-grid">
                                                <div className="form-group">
                                                    <label className="form-label">Fee Head *</label>
                                                    <select
                                                        className="form-control"
                                                        value={head.feeHead}
                                                        onChange={(e) => handleHeadSelect(head.localId, e.target.value)}
                                                    >
                                                        <option value="">Select Fee Head</option>
                                                        {feeHeadsMaster.map((fh) => (
                                                            <option key={fh._id} value={fh._id}>
                                                                {fh.name} ({fh.category})
                                                            </option>
                                                        ))}
                                                        <option value="__NEW__" style={{ fontWeight: "bold", color: "var(--primary)" }}>
                                                            + Add New Master Fee Head...
                                                        </option>
                                                    </select>
                                                </div>

                                                <div className="form-group">
                                                    <label className="form-label">Fee Type</label>
                                                    <select
                                                        className="form-control"
                                                        value={head.feeType}
                                                        onChange={(e) => updateHead(head.localId, "feeType", e.target.value)}
                                                    >
                                                        {FEE_TYPE_OPTIONS.map((t) => (
                                                            <option key={t} value={t}>
                                                                {t}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="form-group">
                                                    <label className="form-label">Default Amount (₹) *</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={head.defaultAmount}
                                                        onChange={(e) => updateHead(head.localId, "defaultAmount", e.target.value)}
                                                        placeholder="0.00"
                                                    />
                                                    <span className="form-hint">Used when no override matches a student.</span>
                                                </div>
                                            </div>

                                            <div style={{ marginTop: "0.75rem" }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)" }}>
                                                        OVERRIDES (optional — most specific match wins)
                                                    </span>
                                                    <button type="button" className="btn btn-outline btn-sm" onClick={() => addOverride(head.localId)}>
                                                        <FiPlusCircle size={12} style={{ marginRight: 4 }} />
                                                        Add Override
                                                    </button>
                                                </div>

                                                {head.overrides.length === 0 ? (
                                                    <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                                                        No overrides — every student pays the default amount above.
                                                    </p>
                                                ) : (
                                                    <div style={{ display: "grid", gap: "0.5rem" }}>
                                                        {head.overrides.map((o) => (
                                                            <div
                                                                key={o.localId}
                                                                style={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: "1fr 1fr 1fr auto",
                                                                    gap: "0.5rem",
                                                                    alignItems: "center",
                                                                    background: "#f8fafc",
                                                                    padding: "0.5rem",
                                                                    borderRadius: 8,
                                                                }}
                                                            >
                                                                <select
                                                                    className="form-control"
                                                                    value={o.admissionCategory}
                                                                    onChange={(e) =>
                                                                        updateOverride(head.localId, o.localId, "admissionCategory", e.target.value)
                                                                    }
                                                                >
                                                                    <option value="">Any Category</option>
                                                                    {ADMISSION_CATEGORY_OPTIONS.map((c) => (
                                                                        <option key={c} value={c}>
                                                                            {c}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <select
                                                                    className="form-control"
                                                                    value={o.studentType}
                                                                    onChange={(e) => updateOverride(head.localId, o.localId, "studentType", e.target.value)}
                                                                >
                                                                    <option value="">Any Student Type</option>
                                                                    {STUDENT_TYPE_OPTIONS.map((t) => (
                                                                        <option key={t} value={t}>
                                                                            {t}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    placeholder="Amount (₹)"
                                                                    value={o.amount}
                                                                    onChange={(e) => updateOverride(head.localId, o.localId, "amount", e.target.value)}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline btn-sm"
                                                                    onClick={() => removeOverride(head.localId, o.localId)}
                                                                >
                                                                    <FiTrash2 size={13} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        <button type="button" className="btn btn-outline" onClick={addHead} style={{ justifySelf: "start" }}>
                            <FiPlusCircle size={14} style={{ marginRight: 6 }} />
                            Add Fee Head
                        </button>
                    </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/settings/fee-structures")}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? "Saving..." : isEdit ? "Update Structure" : "Save Structure"}
                    </button>
                </div>
            </form>

            {showNewHeadModal && (
                <div className="modal-overlay" onClick={() => !savingNewHead && setShowNewHeadModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 450 }}>
                        <h3>Add New Master Fee Head</h3>
                        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1rem" }}>
                            This will add a new fee head to the master catalog.
                        </p>
                        <form onSubmit={handleCreateMasterHead}>
                            <div className="form-group" style={{ marginBottom: "0.75rem" }}>
                                <label className="form-label">Head Name *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. Training & Placement Fee"
                                    value={newHeadData.name}
                                    onChange={(e) => setNewHeadData({ ...newHeadData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group" style={{ marginBottom: "0.75rem" }}>
                                <label className="form-label">Category</label>
                                <select
                                    className="form-control"
                                    value={newHeadData.category}
                                    onChange={(e) => setNewHeadData({ ...newHeadData, category: e.target.value })}
                                >
                                    <option value="Academic">Academic (Academic Tab)</option>
                                    <option value="Exam">Exam (Academic Tab)</option>
                                    <option value="Hostel">Hostel (Others Tab)</option>
                                    <option value="Fine">Fine (Others Tab)</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                                <label className="form-label">Fee Type</label>
                                <select
                                    className="form-control"
                                    value={newHeadData.feeType}
                                    onChange={(e) => setNewHeadData({ ...newHeadData, feeType: e.target.value })}
                                >
                                    <option value="Non-refundable">Non-refundable</option>
                                    <option value="Refundable">Refundable</option>
                                </select>
                            </div>
                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn btn-outline"
                                    onClick={() => setShowNewHeadModal(false)}
                                    disabled={savingNewHead}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={savingNewHead}>
                                    {savingNewHead ? "Creating..." : "Create Fee Head"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}